import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, Building2, Sparkles } from "lucide-react";
import CountUp from "@/components/ui/CountUp";

export default function StatsSection() {
  const [stats, setStats] = useState({ years_experience: 20, projects_count: 14 });

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      if (r.data) {
        setStats({
          years_experience: r.data.years_experience ?? 20,
          projects_count: r.data.projects_count ?? 14,
        });
      }
    }).catch(() => {});
  }, []);

  return (
    <section
      id="stats"
      data-testid="stats-section"
      className="relative py-16 sm:py-24 bg-[#0A1128] text-white overflow-hidden"
    >
      <div className="absolute -top-24 -left-20 w-96 h-96 rounded-full opacity-25 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(140px)" }} />
      <div className="absolute -bottom-32 -right-20 w-[28rem] h-[28rem] rounded-full opacity-15 pointer-events-none" style={{ background: "#FFDCA8", filter: "blur(160px)" }} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-[10px] font-bold tracking-[0.32em] uppercase text-[#67E8F9] flex items-center justify-center gap-2">
            <Sparkles size={12} /> Notre track record
          </div>
          <h2 className="display mt-3 text-3xl sm:text-5xl font-extrabold leading-[1.05]">
            <span className="text-white">Une expertise </span>
            <em className="not-italic relative">
              <span className="relative z-10 text-[#67E8F9]">éprouvée</span>
              <span className="absolute inset-x-0 bottom-1 h-2.5 sm:h-3 bg-[#67E8F9]/25 -z-0 rounded-sm" />
            </em>
            <span className="text-white">.</span>
          </h2>
          <p className="mt-4 text-white/75 text-sm sm:text-base">
            Des années de terrain · des entrepreneurs qui nous font confiance · des résultats concrets.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-8">
          <StatCard
            icon={Award}
            value={stats.years_experience}
            suffix=" ans"
            label="d'expérience"
            sub="conseil, marketing & digital"
            accent="#67E8F9"
            testId="stat-years"
          />
          <StatCard
            icon={Building2}
            value={stats.projects_count}
            label="entreprises accompagnées"
            sub="TPE, PME & dirigeants"
            accent="#FFDCA8"
            testId="stat-projects"
          />
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, value, suffix = "", label, sub, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="relative group rounded-3xl p-5 sm:p-8 bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:border-white/25 hover:bg-white/[0.06] transition-all overflow-hidden"
    >
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ background: accent, filter: "blur(50px)" }}
      />
      <div className="relative flex items-start gap-3 sm:gap-4">
        <div
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-2xl"
          style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}
        >
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="display font-black leading-none tracking-tight text-4xl sm:text-6xl">
            <CountUp end={value} suffix={suffix} data-testid={`${testId}-number`} className="tabular-nums" />
          </div>
          <div className="mt-2 sm:mt-3">
            <div className="text-[13px] sm:text-base font-bold text-white">{label}</div>
            <div className="text-[11px] sm:text-xs text-white/55 mt-0.5">{sub}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
